import { createReference, getExtensionValue, getReferenceString } from '@medplum/core';
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Flex,
  Group,
  Menu,
  Stack,
  Text,
  Textarea,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import { IconDotsVertical, IconLink, IconNote, IconPencil, IconTrash, IconX } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { EncounterTimelineGroup, TimelineNote } from '@/components/Timeline/types';
import { HighlightableText } from '@/components/HighlightableText';
import { EncounterStatusBadge } from '@/components/EncounterStatusBadge';
import { Encounter, PlanDefinition, Provenance, QuestionnaireResponseItem, Reference, Task } from '@medplum/fhirtypes';
import { EncounterDownloadButton } from '@/components/EncounterDownloadButton';
import { EncounterHistory } from './EncounterHistory';
import { EncounterStatus } from '@bariendo/lib/constants';
import { DateTimeTooltip } from '@/components/Timeline/TimelineItems/DateTimeTooltip';
import { HighlightableItem } from '@/components/Timeline/TimelineItems/HighlightableItem';
import { TimelineCard } from '@/components/Timeline/TimelineItems/TimelineCard';
import { useTimelineContext } from '@/hooks/useTimelineContext';
import { useSignEncounter } from '@/pages/PatientTimelinePage/hooks/useSignEncounter';
import { useTimelineNote } from '@/hooks/useTimelineNote';
import {
  getQuestionnaireOutputs,
  formatQuestionnaireAnswer,
} from '@/pages/PatientTimelinePage/services/questionnaireResponses';
import { orderTasksByPlanDefinition } from '@bariendo/lib/order-tasks';
import { useMedplum } from '@medplum/react';
import { BodyDiagramView } from '@/components/Timeline/BodyDiagramView';
import { isBodyDiagramData, parseBodyDiagramData } from '@/pages/PatientTimelinePage/services/bodyDiagram';
import { hasHealthieSourceOrIdentifier } from '@bariendo/lib/healthie-migration';
import { DeleteConfirmationPopover } from '@/components/DeleteConfirmationPopover';
import { useDeleteEncounterDraft } from '@/pages/PatientTimelinePage/hooks/useDeleteEncounterDraft';
import { DATA_FROM_HEALTHIE_EXTENSION } from '@bariendo/lib/extensions';
import { TeammateAvatar } from '@/components/TeamAvatar';

interface EncounterTimelineItemProps {
  group: EncounterTimelineGroup;
  highlightRef?: string | null;
  hasActiveFilters?: boolean;
  searchTerm?: string;
  copyDirectLink: (itemReference: string) => Promise<void>;
  handleNavigateToItem: (itemReference: string) => void;
}

interface EncounterTimelineHeaderProps {
  group: EncounterTimelineGroup;
  orderedTasks: Task[];
  onSign: (encounter: Encounter, lock: boolean) => void;
  isHealthieMigrated: boolean;
  provenance?: Provenance;
  notes: TimelineNote[];
  copyDirectLink: (itemReference: string) => Promise<void>;
  onShowAddendumForm: () => void;
}

export function EncounterTimelineItem(props: EncounterTimelineItemProps) {
  const { group, highlightRef, searchTerm, copyDirectLink, handleNavigateToItem } = props;
  const { isSigning, handleSignEncounter } = useSignEncounter(handleNavigateToItem);
  const { timelineContext } = useTimelineContext();
  const medplum = useMedplum();

  const encounter = group.items[0];
  const encounterRef = getReferenceString(encounter.reference);
  const encounterResource = encounter.encounter;
  const notes = group.notes ?? [];
  const [showEncounterDetails, setShowEncounterDetails] = useState(false);
  const [orderedTasks, setOrderedTasks] = useState<Task[]>(encounterResource.tasks ?? []);
  const [showAddendumForm, setShowAddendumForm] = useState(false);

  // Check if this encounter was migrated from Healthie
  const isHealthieMigrated = hasHealthieSourceOrIdentifier(encounterResource);

  // Order tasks according to PlanDefinition action order
  useEffect(() => {
    const allTasks = encounterResource.tasks ?? [];
    if (allTasks.length === 0) {
      setOrderedTasks([]);
      return;
    }

    // Get PlanDefinition from the first task's basedOn field to order tasks
    if (allTasks[0]?.basedOn?.[0]) {
      const orderTasks = async () => {
        try {
          const planDefinition = await medplum.readReference<PlanDefinition>(
            allTasks[0].basedOn![0] as Reference<PlanDefinition>
          );
          if (planDefinition) {
            const ordered = await orderTasksByPlanDefinition(medplum, allTasks, planDefinition);
            setOrderedTasks(ordered);
          } else {
            setOrderedTasks(allTasks);
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.warn('Failed to read PlanDefinition for task ordering:', error);
          // Continue with unordered tasks if PlanDefinition read fails
          setOrderedTasks(allTasks);
        }
      };

      void orderTasks();
    } else {
      setOrderedTasks(allTasks);
    }
  }, [medplum, encounterResource.tasks]);

  const handleAddNote = () => {
    if (!encounterRef) return;
    timelineContext.refresh();
    handleNavigateToItem(encounterRef);
  };

  const handleSign = async (encounter: Encounter) => {
    if (isSigning || !encounter) return;
    await handleSignEncounter(encounter);
  };

  return (
    <HighlightableItem itemRef={encounterRef!} highlightRef={highlightRef}>
      <TimelineCard
        header={
          <EncounterTimelineHeader
            group={group}
            orderedTasks={orderedTasks}
            onSign={handleSign}
            isHealthieMigrated={isHealthieMigrated}
            provenance={encounterResource.history?.[0]}
            notes={notes}
            copyDirectLink={copyDirectLink}
            onShowAddendumForm={() => setShowAddendumForm(true)}
          />
        }
      >
        {showEncounterDetails ? (
          <Stack gap="md">
            <EncounterQuestionnaireResponses tasks={orderedTasks} searchTerm={searchTerm} />
          </Stack>
        ) : (
          <Box>
            <Stack gap="md">
              <EncounterQuestionnaireResponses tasks={orderedTasks} searchTerm={searchTerm} maxOutputs={0} />
            </Stack>
          </Box>
        )}

        <UnstyledButton onClick={() => setShowEncounterDetails((prev) => !prev)} c="#246A7A" fz="sm" fw={600}>
          {showEncounterDetails ? 'Show less...' : 'Show more...'}
        </UnstyledButton>

        <EncounterHistory
          provenance={encounterResource.history?.[0]}
          isFinished={encounterResource.status === EncounterStatus.FINISHED}
          notes={notes}
        />

        {showAddendumForm && (
          <AddendumForm
            encounter={encounterResource}
            onAddendum={handleAddNote}
            onCloseAddendumForm={() => setShowAddendumForm(false)}
          />
        )}
      </TimelineCard>
    </HighlightableItem>
  );
}

function EncounterTimelineHeader({
  group,
  orderedTasks,
  isHealthieMigrated = false,
  notes,
  copyDirectLink,
  onShowAddendumForm,
}: EncounterTimelineHeaderProps) {
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  const encounterItem = group.items[0];
  const encounter = encounterItem?.encounter;
  const encounterReference = getReferenceString(encounterItem.reference);
  const isDraftEncounter =
    encounter?.status === EncounterStatus.PLANNED || encounter?.status === EncounterStatus.IN_PROGRESS;
  const participant = encounter?.history?.[0]?.agent?.[0]?.who ?? encounter?.participant?.[0]?.individual;

  let planDefinitionTitle = encounter?.tasks?.[0]?.basedOn?.[0]?.display;

  if (isHealthieMigrated) {
    const healthieData = getExtensionValue(encounter?.tasks?.[0], DATA_FROM_HEALTHIE_EXTENSION);
    if (healthieData) {
      const healthieDataObject = JSON.parse(healthieData as string);
      planDefinitionTitle = healthieDataObject.custom_module_form?.name;
    }
  }

  const { isDeleting, handleDeleteEncounterDraft } = useDeleteEncounterDraft();

  const handleDeleteConfirmation = async () => {
    await handleDeleteEncounterDraft(encounter);
    setShowDeleteConfirmation(false);
  };

  const handleCopyLink = () => {
    if (encounterReference) {
      copyDirectLink(encounterReference);
    }
  };

  return (
    <Group justify="space-between" align="center">
      <Group>
        <Flex gap="xs" align="center">
          <Stack gap={2}>
            <Flex gap="xs" align="center">
              <Text size="md" fw={700} style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {planDefinitionTitle || 'Chart note'}
              </Text>
              <EncounterStatusBadge status={encounter?.status} />
              {isHealthieMigrated && (
                <Badge size="sm" color="blue" variant="light">
                  Healthie
                </Badge>
              )}
            </Flex>
          </Stack>
        </Flex>
      </Group>
      <Group gap="md" align="center">
        <DeleteConfirmationPopover
          onConfirm={handleDeleteConfirmation}
          isLoading={isDeleting}
          title="Delete this draft chart?"
          description="This will permanently delete the draft chart note and its associated responses."
          opened={showDeleteConfirmation}
          onClose={() => setShowDeleteConfirmation(false)}
        >
          <ActionMenu
            encounter={encounter}
            orderedTasks={orderedTasks}
            notes={notes}
            isEditable={encounter.status !== EncounterStatus.FINISHED && !isHealthieMigrated}
            canDeleteDraft={isDraftEncounter && !isHealthieMigrated}
            onCopyLink={handleCopyLink}
            onDeleteDraft={() => setShowDeleteConfirmation(true)}
            onShowAddendumForm={onShowAddendumForm}
          />
        </DeleteConfirmationPopover>

        <DateTimeTooltip date={group.timestamp} />

        <TeammateAvatar practitioner={participant?.resource ?? participant} label={participant?.display} />
      </Group>
    </Group>
  );
}

function ActionMenu({
  onCopyLink,
  onDeleteDraft,
  isEditable,
  canDeleteDraft,
  encounter,
  orderedTasks,
  notes,
  onShowAddendumForm,
}: {
  isEditable: boolean;
  canDeleteDraft?: boolean;
  onCopyLink: () => void;
  onDeleteDraft?: () => void;
  encounter: Encounter & { tasks?: Task[]; history?: Provenance[] };
  orderedTasks: Task[];
  notes: TimelineNote[];
  onShowAddendumForm: () => void;
}) {
  const { onEditEncounter } = useTimelineContext();
  const isHealthieMigrated = hasHealthieSourceOrIdentifier(encounter);

  return (
    <Menu>
      <Menu.Target>
        <ActionIcon variant="subtle" color="gray" size="sm">
          <IconDotsVertical size={24} />
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown>
        {/* TODO: to be implemented in the Task feature phase <Menu.Item leftSection={<IconListCheck size={18} />}>Add Task</Menu.Item> */}
        <Menu.Item leftSection={<IconLink size={18} color="#246A7A" />} onClick={onCopyLink}>
          Copy link
        </Menu.Item>

        <EncounterDownloadButton encounter={encounter} tasks={orderedTasks} notes={notes} />

        {(encounter.status === EncounterStatus.FINISHED || isHealthieMigrated) && (
          <Menu.Item leftSection={<IconNote size={18} color="#246A7A" />} onClick={onShowAddendumForm}>
            Add addendum
          </Menu.Item>
        )}

        {isEditable && (
          <Menu.Item
            leftSection={<IconPencil size={18} color="#246A7A" />}
            onClick={() => onEditEncounter?.(createReference(encounter))}
          >
            Edit
          </Menu.Item>
        )}

        {canDeleteDraft && (
          <Menu.Item leftSection={<IconTrash size={18} color="var(--mantine-color-red-5)" />} onClick={onDeleteDraft}>
            <Text c="red.6">Delete</Text>
          </Menu.Item>
        )}
      </Menu.Dropdown>
    </Menu>
  );
}

function EncounterQuestionnaireResponses({
  tasks = [],
  searchTerm,
  maxOutputs,
}: {
  tasks?: Task[];
  searchTerm?: string;
  maxOutputs?: number;
}) {
  const questionnaireOutputs = getQuestionnaireOutputs(tasks);

  if (questionnaireOutputs.length === 0) {
    return null;
  }

  const outputsToRender =
    typeof maxOutputs === 'number' ? questionnaireOutputs.slice(0, maxOutputs) : questionnaireOutputs;

  return (
    <Stack>
      {outputsToRender.map(({ key, response }) => (
        <Stack
          key={key}
          gap={0}
          style={{
            borderBottom: '1px solid var(--mantine-color-gray-3)',
          }}
        >
          {flattenGroupItems(response?.item ?? []).map(({ item, isGroupHeader }, index) => (
            <Group key={`${response?.id ?? key}-${item.linkId}-${index}`} p="xs" gap="0" wrap="nowrap">
              <div style={{ flex: 2 }}>
                <HighlightableText
                  text={item.text ?? ''}
                  searchTerm={searchTerm ?? ''}
                  size="sm"
                  style={{ fontWeight: isGroupHeader ? 700 : 600, paddingLeft: isGroupHeader ? 0 : 12 }}
                  flex={1}
                />
              </div>
              <div style={{ flex: 1 }}>
                {!isGroupHeader && <QuestionnaireResponseItemView item={item} searchTerm={searchTerm} />}
              </div>
            </Group>
          ))}
        </Stack>
      ))}
    </Stack>
  );
}

function flattenGroupItems(
  items: QuestionnaireResponseItem[]
): { item: QuestionnaireResponseItem; isGroupHeader: boolean }[] {
  return items.flatMap((item) => {
    if (item.item && item.item.length > 0) {
      return [{ item, isGroupHeader: true }, ...item.item.map((child) => ({ item: child, isGroupHeader: false }))];
    }

    return [{ item, isGroupHeader: false }];
  });
}

function QuestionnaireResponseItemView({ item, searchTerm }: { item: QuestionnaireResponseItem; searchTerm?: string }) {
  // Check for body diagram data in answers
  const bodyDiagramAnswers =
    item.answer?.filter((answer) => answer.valueString && isBodyDiagramData(answer.valueString)) ?? [];

  const regularAnswers =
    item.answer?.filter((answer) => !answer.valueString || !isBodyDiagramData(answer.valueString)) ?? [];

  const answerNodes =
    regularAnswers
      .map(formatQuestionnaireAnswer)
      .filter((answer): answer is string => Boolean(answer))
      .map((answer, answerIndex) => (
        <HighlightableText
          key={`${item.linkId}-${answerIndex}`}
          text={answer}
          searchTerm={searchTerm ?? ''}
          size="sm"
        />
      )) ?? [];

  // Render body diagrams
  const bodyDiagramNodes = bodyDiagramAnswers
    .map((answer, index) => {
      const diagramData = parseBodyDiagramData(answer.valueString!);
      if (!diagramData) return null;

      return (
        <Box key={`body-diagram-${item.linkId}-${index}`} mt="xs">
          <BodyDiagramView data={diagramData} searchTerm={searchTerm} />
        </Box>
      );
    })
    .filter(Boolean);
  const answerItemNodes =
    item.answer?.flatMap((answer, answerIndex) =>
      (answer.item ?? []).map((nestedItem) => (
        <QuestionnaireResponseItemView
          key={`${item.linkId}-${answerIndex}-${nestedItem.linkId}`}
          item={nestedItem}
          searchTerm={searchTerm}
        />
      ))
    ) ?? [];

  const childNodes =
    item.item?.map((child) => (
      <QuestionnaireResponseItemView key={`${item.linkId}-${child.linkId}`} item={child} searchTerm={searchTerm} />
    )) ?? [];

  const nodes = [...answerNodes, ...bodyDiagramNodes, ...answerItemNodes, ...childNodes];

  if (nodes.length === 0) {
    return null;
  }

  return <>{nodes}</>;
}

interface AddendumFormProps {
  encounter: Encounter;
  onAddendum: () => void;
  onCloseAddendumForm: () => void;
}

function AddendumForm({ encounter, onAddendum, onCloseAddendumForm }: AddendumFormProps) {
  const { isSavingNote, createNote } = useTimelineNote();
  const [addendumContent, setAddendumContent] = useState('');

  const handleAddAddendum = async () => {
    const success = await createNote(createReference(encounter), addendumContent);
    if (success) {
      setAddendumContent('');
      onAddendum();
    }
  };

  return (
    <Box mt="md">
      <Group justify="space-between" align="center">
        <Text fw={600} size="14px" mb="xs">
          Add addendum
        </Text>

        <Tooltip label="Close">
          <ActionIcon variant="subtle" color="gray" size="sm" onClick={onCloseAddendumForm}>
            <IconX size={18} />
          </ActionIcon>
        </Tooltip>
      </Group>

      <Textarea
        placeholder="Add addendum"
        value={addendumContent}
        onChange={(e) => setAddendumContent(e.target.value)}
        style={{ border: '1px solid #868E96', borderRadius: '6px' }}
      />

      <Stack align="flex-end" mt="md">
        <Box>
          <Button disabled={isSavingNote || !addendumContent.trim()} color="#223843" onClick={handleAddAddendum}>
            Add addendum
          </Button>
        </Box>
      </Stack>
    </Box>
  );
}
